import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import type { OrderbookResponse } from '../../types/orderbook';
import type { ArbitrageOpportunity } from '../../types';
import { computeDisplayProperties } from '../../types/opportunity';
import {
  fetchOrderbook,
  fetchOrderbookPair,
  fetchFuturesOrderbookWithTicker,
  type FuturesBookTickerResponse
} from '../../services/orderbookService';
import { OrderbookDepthTable } from './OrderbookDepthTable';
import {
  formatOrderbookPrice,
  formatPercentage,
  formatTotalValue,
  formatVolume,
} from '../../utils/formatters';
import { getExchangeLogo, EXCHANGE_NAMES, ExchangeUrls } from '../../utils/exchangeUrls';
import TradingChart from './TradingChart';

type OrderbookParams = {
  exchange?: string;
  base?: string;
  quote?: string;
  symbol?: string;
  exchange1?: string;
  base1?: string;
  quote1?: string;
  symbol1?: string;
  exchange2?: string;
  base2?: string;
  quote2?: string;
  symbol2?: string;
};

const MAX_LEVELS_DISPLAYED = 30;
const POLL_INTERVAL_MS = 1_000;
const STORAGE_KEY_AUTO_REFRESH = 'orderbook:autoRefresh';
const STORAGE_KEY_DEPTH = 'orderbook:depth';

function formatTimestamp(timestamp: number, locale: string) {
  if (!timestamp) {
    return '–';
  }
  try {
    const normalizedTimestamp = timestamp > 1e13 ? Math.floor(timestamp / 1_000) : timestamp;

    return new Date(normalizedTimestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(timestamp);
  }
}

function combineSymbol(symbol?: string, base?: string, quote?: string): string | undefined {
  if (symbol) {
    return symbol.toUpperCase();
  }
  if (base && quote) {
    return `${base}${quote}`.toUpperCase();
  }
  return undefined;
}

function normalizeExchangeIdentifier(value?: string): string {
  return value ? value.toLowerCase().replace(/[^a-z0-9]/g, '') : '';
}

function findExchangeIndex(value?: string): number {
  const normalized = normalizeExchangeIdentifier(value);
  if (!normalized) {
    return -1;
  }
  return EXCHANGE_NAMES.findIndex((name) => normalizeExchangeIdentifier(name) === normalized);
}

interface OrderbookCardState {
  orderbook: OrderbookResponse | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  hasFetched: boolean;
  opportunity: ArbitrageOpportunity | null;
  bookTicker?: FuturesBookTickerResponse;
}

interface OrderbookPairState {
  primary: OrderbookCardState;
  secondary: OrderbookCardState;
  conversionRate: number | null;
}

function useOrderbookStream(
  exchange: string | undefined,
  symbol: string | undefined,
  genericError: string | undefined,
  autoRefresh: boolean,
  enabled = true,
  isFutures = false,
): OrderbookCardState {
  const [orderbook, setOrderbook] = useState<OrderbookResponse | null>(null);
  const [bookTicker, setBookTicker] = useState<FuturesBookTickerResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchData = useCallback(
    async (silent = false) => {
      if (!enabled || !exchange || !symbol) {
        setOrderbook(null);
        setBookTicker(undefined);
        setError(null);
        setIsLoading(false);
        setHasFetched(false);
        return;
      }

      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      try {
        if (isFutures) {
          const futuresData = await fetchFuturesOrderbookWithTicker(exchange, symbol);
          setOrderbook(futuresData.orderbook);
          setBookTicker(futuresData.bookTicker);
        } else {
          const data = await fetchOrderbook(exchange, symbol);
          setOrderbook(data);
          setBookTicker(undefined);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch orderbook', err);
        const message = err instanceof Error ? err.message : genericError ?? 'Failed to fetch orderbook';
        setError(message);
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
        setHasFetched(true);
      }
    },
    [exchange, symbol, genericError, isFutures],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void fetchData(false);
  }, [enabled, fetchData]);

  useEffect(() => {
    if (!enabled || !autoRefresh || !exchange || !symbol || !hasFetched) {
      return;
    }

    void fetchData(true);

    const intervalId = window.setInterval(() => {
      void fetchData(true);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [enabled, autoRefresh, exchange, symbol, hasFetched, fetchData]);

  return {
    orderbook,
    isLoading,
    error,
    refresh: () => {
      void fetchData(false);
    },
    hasFetched,
    opportunity: null,
    bookTicker,
  };
}

function useOrderbookPair(
  primaryExchange: string | undefined,
  primarySymbol: string | undefined,
  secondaryExchange: string | undefined,
  secondarySymbol: string | undefined,
  genericError: string | undefined,
  missingMessage: string,
  autoRefresh: boolean,
): OrderbookPairState {
  const [primaryOrderbook, setPrimaryOrderbook] = useState<OrderbookResponse | null>(null);
  const [secondaryOrderbook, setSecondaryOrderbook] = useState<OrderbookResponse | null>(null);
  const [primaryError, setPrimaryError] = useState<string | null>(null);
  const [secondaryError, setSecondaryError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [opportunity, setOpportunity] = useState<ArbitrageOpportunity | null>(null);
  const [conversionRate, setConversionRate] = useState<number | null>(null);

  const normalizedPrimaryExchange = primaryExchange?.toLowerCase();
  const normalizedSecondaryExchange = secondaryExchange?.toLowerCase();

  const fetchData = useCallback(
    async (silent = false) => {
      if (!normalizedPrimaryExchange || !primarySymbol || !normalizedSecondaryExchange || !secondarySymbol) {
        setPrimaryOrderbook(null);
        setSecondaryOrderbook(null);
        setPrimaryError(null);
        setSecondaryError(null);
        setIsLoading(false);
        setHasFetched(false);
        return;
      }

      if (!silent) {
        setIsLoading(true);
        setPrimaryError(null);
        setSecondaryError(null);
      }

      try {
        const payload = await fetchOrderbookPair(
          normalizedPrimaryExchange,
          primarySymbol,
          normalizedSecondaryExchange,
          secondarySymbol,
        );

        const left = payload.left ?? null;
        const right = payload.right ?? null;

        setPrimaryOrderbook(left);
        setSecondaryOrderbook(right);
        setOpportunity(payload.opportunity ?? null);
        setConversionRate(
          typeof payload.conversion_rate === 'number' && Number.isFinite(payload.conversion_rate)
            ? payload.conversion_rate
            : null,
        );

        setPrimaryError(left ? null : missingMessage);
        setSecondaryError(right ? null : missingMessage);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : genericError ?? 'Failed to fetch orderbooks';
        setPrimaryError(message);
        setSecondaryError(message);
        setOpportunity(null);
        setConversionRate(null);
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
        setHasFetched(true);
      }
    },
    [
      normalizedPrimaryExchange,
      primarySymbol,
      normalizedSecondaryExchange,
      secondarySymbol,
      genericError,
      missingMessage,
    ],
  );

  useEffect(() => {
    void fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh || !normalizedPrimaryExchange || !primarySymbol || !normalizedSecondaryExchange || !secondarySymbol || !hasFetched) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchData(true);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    autoRefresh,
    normalizedPrimaryExchange,
    primarySymbol,
    normalizedSecondaryExchange,
    secondarySymbol,
    hasFetched,
    fetchData,
  ]);

  const primaryState: OrderbookCardState = useMemo(
    () => ({
      orderbook: primaryOrderbook,
      isLoading,
      error: primaryError,
      hasFetched,
      refresh: () => {
        void fetchData(false);
      },
      opportunity,
    }),
    [primaryOrderbook, isLoading, primaryError, hasFetched, fetchData, opportunity],
  );

  const secondaryState: OrderbookCardState = useMemo(
    () => ({
      orderbook: secondaryOrderbook,
      isLoading,
      error: secondaryError,
      hasFetched,
      refresh: () => {
        void fetchData(false);
      },
      opportunity,
    }),
    [secondaryOrderbook, isLoading, secondaryError, hasFetched, fetchData, opportunity],
  );

  return { primary: primaryState, secondary: secondaryState, conversionRate };
}

interface CardMeta {
  title: string;
  exchange?: string;
  pair?: string;
  quote?: string;
  apiEndpoint?: string | null;
  maxRows: number;
}

interface OrderbookCardProps {
  meta: CardMeta;
  state: OrderbookCardState;
  headers: {
    price: string;
    size: string;
    total: string;
  };
  noDataTitle: string;
  noDataDescription: string;
  t: TFunction;
  isFutures?: boolean;
}

const OrderbookCard: React.FC<OrderbookCardProps> = ({ meta, state, headers, noDataTitle, noDataDescription, t, isFutures = false }) => {
  const { orderbook, isLoading, error, refresh, opportunity, bookTicker } = state;

  const displayExchange = orderbook?.exchange?.toUpperCase() ?? meta.exchange ?? '';
  const displayPair = orderbook?.pair ?? meta.pair ?? '';
  const displayQuote = orderbook?.quote?.toUpperCase() ?? meta.quote ?? '';

  const asks = orderbook?.asks ?? [];
  const bids = orderbook?.bids ?? [];

  const askHighlightPrices = useMemo(() => {
    if (!opportunity || !Array.isArray(opportunity.buy_levels)) {
      return undefined;
    }
    const prices = opportunity.buy_levels
      .map((level) => level?.price)
      .filter((price): price is number => typeof price === 'number' && Number.isFinite(price));
    return prices.length ? prices : undefined;
  }, [opportunity]);

  const bidHighlightPrices = useMemo(() => {
    if (!opportunity || !Array.isArray(opportunity.sell_levels)) {
      return undefined;
    }
    const prices = opportunity.sell_levels
      .map((level) => level?.price)
      .filter((price): price is number => typeof price === 'number' && Number.isFinite(price));
    return prices.length ? prices : undefined;
  }, [opportunity]);

  const bestAsk = asks.reduce((min, level) => {
    if (!min || level.price < min.price) {
      return level;
    }
    return min;
  }, undefined as OrderbookResponse['asks'][number] | undefined);

  const bestBid = bids.reduce((max, level) => {
    if (!max || level.price > max.price) {
      return level;
    }
    return max;
  }, undefined as OrderbookResponse['bids'][number] | undefined);

  const midPrice =
    bestAsk && bestBid ? (bestAsk.price + bestBid.price) / 2 : bestAsk?.price ?? bestBid?.price ?? 0;
  const spreadValue =
    bestAsk && bestBid ? Math.max(bestAsk.price - bestBid.price, 0) : 0;
  const spreadPercent =
    bestAsk && bestBid && bestAsk.price > 0
      ? ((bestAsk.price - bestBid.price) / bestAsk.price) * 100
      : 0;

  const showInitialSpinner = isLoading && !orderbook;

  return (
    <Paper
      elevation={4}
      sx={{
        flex: 1,
        px: { xs: 1, sm: 2 },
        py: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        background: 'linear-gradient(180deg, rgba(15,18,28,0.9) 0%, rgba(9,12,20,0.95) 100%)',
        border: '1px solid rgba(255,255,255,0.05)',
        minWidth: 0,
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {displayExchange}
          </Typography>
          <Typography variant="h5" color="text.primary" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {displayPair}
          </Typography>
        </Stack>

        {showInitialSpinner ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && (
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button color="inherit" size="small" onClick={refresh}>
                    {t('common.retry')}
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {orderbook ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: 'auto auto auto',
                  rowGap: 1.5,
                }}
              >
                <OrderbookDepthTable
                  levels={orderbook.asks}
                  side="asks"
                  maxRows={meta.maxRows}
                  headers={headers}
                  highlightPrices={askHighlightPrices}
                  highlightTolerance={1e-8}
                />

                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.5,
                    textAlign: 'center',
                    borderRadius: 2,
                    background: 'rgba(17, 24, 39, 0.85)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: 'common.white',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatOrderbookPrice(midPrice)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {t('orderbookPage.spreadLabel', {
                      value: formatOrderbookPrice(spreadValue),
                      quote: displayQuote || '',
                      percent: spreadPercent.toFixed(3),
                    })}
                  </Typography>
                  {bestBid && bestAsk && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                      {t('orderbookPage.bestBidAsk', {
                        bid: `${formatOrderbookPrice(bestBid.price)} (${formatVolume(bestBid.volume, 4)})`,
                        ask: `${formatOrderbookPrice(bestAsk.price)} (${formatVolume(bestAsk.volume, 4)})`,
                      })}
                    </Typography>
                  )}
                  {isFutures && bookTicker && bestBid && bestAsk && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                        {t('orderbookPage.bestBidAsk', {
                          bid: `${formatOrderbookPrice(bookTicker.bid_price)} (${formatVolume(bookTicker.bid_qty, 4)})`,
                          ask: `${formatOrderbookPrice(bookTicker.ask_price)} (${formatVolume(bookTicker.ask_qty, 4)})`,
                        })}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          bestBid.price > bookTicker.bid_price || bestAsk.price < bookTicker.ask_price
                            ? 'error.main'
                            : bestBid.price < bookTicker.bid_price || bestAsk.price > bookTicker.ask_price
                              ? 'success.main'
                              : 'text.secondary'
                        }
                        sx={{ display: 'block', fontVariantNumeric: 'tabular-nums', mt: 0.25 }}
                      >
                        Δ Bid: {formatOrderbookPrice(Math.abs(bestBid.price - bookTicker.bid_price))} ({((Math.abs(bestBid.price - bookTicker.bid_price) / bookTicker.bid_price) * 100).toFixed(3)}%) | Δ Ask: {formatOrderbookPrice(Math.abs(bestAsk.price - bookTicker.ask_price))} ({((Math.abs(bestAsk.price - bookTicker.ask_price) / bookTicker.ask_price) * 100).toFixed(3)}%)
                      </Typography>
                    </Box>
                  )}
                </Paper>

                <OrderbookDepthTable
                  levels={orderbook.bids}
                  side="bids"
                  maxRows={meta.maxRows}
                  headers={headers}
                  highlightPrices={bidHighlightPrices}
                  highlightTolerance={1e-8}
                />
              </Box>
            ) : (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="h6">{noDataTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {noDataDescription}
                </Typography>
                {meta.apiEndpoint && (
                  <Link href={meta.apiEndpoint} target="_blank" rel="noopener noreferrer">
                    {t('orderbookPage.openApiLink')}
                  </Link>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

interface OrderbookPageProps {
  isFutures?: boolean;
}

const OrderbookPage: React.FC<OrderbookPageProps> = ({ isFutures = false }) => {
  const params = useParams<OrderbookParams>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY_AUTO_REFRESH);
    return stored === 'true';
  });

  const [depthLimit, setDepthLimit] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return MAX_LEVELS_DISPLAYED;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY_DEPTH);
    const parsed = stored ? Number(stored) : MAX_LEVELS_DISPLAYED;
    if (Number.isNaN(parsed)) {
      return MAX_LEVELS_DISPLAYED;
    }
    return Math.min(Math.max(parsed, 1), MAX_LEVELS_DISPLAYED);
  });

  const primaryExchangeParam = params.exchange ?? params.exchange1;
  const primaryBaseParam = params.base ?? params.base1;
  const primaryQuoteParam = params.quote ?? params.quote1;
  const primarySymbolParam = combineSymbol(params.symbol ?? params.symbol1, primaryBaseParam, primaryQuoteParam);

  const secondaryExchangeParam = params.exchange2;
  const secondaryBaseParam = params.base2;
  const secondaryQuoteParam = params.quote2;
  const secondarySymbolParam = combineSymbol(params.symbol2, secondaryBaseParam, secondaryQuoteParam);

  const normalizedPrimaryExchange = primaryExchangeParam?.toLowerCase();
  const normalizedSecondaryExchange = secondaryExchangeParam?.toLowerCase();

  const isComparison = Boolean(normalizedSecondaryExchange && secondarySymbolParam);
  const isSymbolComparisonRoute = Boolean(params.symbol1 || params.symbol2);
  const primaryExchangeIndex = findExchangeIndex(primaryExchangeParam);
  const secondaryExchangeIndex = findExchangeIndex(secondaryExchangeParam);

  const genericError = t('orderbookPage.genericError');

  const primaryState = useOrderbookStream(
    normalizedPrimaryExchange,
    primarySymbolParam,
    genericError,
    autoRefreshEnabled,
    !isComparison,
    isFutures,
  );

  const pairStates = useOrderbookPair(
    normalizedPrimaryExchange,
    primarySymbolParam,
    normalizedSecondaryExchange,
    secondarySymbolParam,
    genericError,
    t('orderbookPage.noDataDescription'),
    autoRefreshEnabled,
  );

  if (!normalizedPrimaryExchange || !primarySymbolParam) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Stack spacing={2}>
          <Typography variant="h4">{t('orderbookPage.missingParamsTitle')}</Typography>
          <Typography variant="body1" color="text.secondary">
            {t('orderbookPage.missingParamsDescription')}
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            startIcon={<ArrowBackIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('orderbookPage.backToDashboard')}
          </Button>
        </Stack>
      </Container>
    );
  }

  const headers = useMemo(
    () => ({
      price: t('orderbookPage.columns.price'),
      size: t('orderbookPage.columns.size'),
      total: t('orderbookPage.columns.total'),
    }),
    [t],
  );

  if (
    isComparison &&
    normalizedPrimaryExchange === normalizedSecondaryExchange &&
    primarySymbolParam &&
    primarySymbolParam === secondarySymbolParam
  ) {
    return <Navigate to={`/orderbook/${normalizedPrimaryExchange}/${primarySymbolParam}`} replace />;
  }

  const primaryMeta: CardMeta = {
    title: `${(primaryExchangeParam ?? '').toUpperCase()} ${primarySymbolParam}`,
    exchange: primaryExchangeParam?.toUpperCase(),
    pair: primarySymbolParam,
    quote: primaryQuoteParam?.toUpperCase(),
    apiEndpoint: normalizedPrimaryExchange && primarySymbolParam
      ? `/api/orderbook/${normalizedPrimaryExchange}/${primarySymbolParam}`
      : null,
    maxRows: depthLimit,
  };

  const secondaryMeta: CardMeta | null =
    isComparison
      ? {
          title: `${(secondaryExchangeParam ?? '').toUpperCase()} ${secondarySymbolParam}`,
          exchange: secondaryExchangeParam?.toUpperCase(),
          pair: secondarySymbolParam,
          quote: secondaryQuoteParam?.toUpperCase(),
          apiEndpoint: `/api/orderbook/${normalizedSecondaryExchange}/${secondarySymbolParam}`,
          maxRows: depthLimit,
        }
      : null;

const cards = isComparison && secondaryMeta
  ? [
      { key: 'primary', meta: primaryMeta, state: pairStates.primary },
      { key: 'secondary', meta: secondaryMeta, state: pairStates.secondary },
    ]
  : [
      { key: 'primary', meta: primaryMeta, state: primaryState },
    ];

const leftOrderbook = pairStates.primary.orderbook;
const rightOrderbook = pairStates.secondary.orderbook;

const primaryOrderbookForHeader = isComparison
  ? leftOrderbook
  : primaryState.orderbook ?? leftOrderbook;
const primaryHeaderBase = primaryOrderbookForHeader?.base ?? primaryBaseParam ?? null;
const primaryHeaderQuote = primaryOrderbookForHeader?.quote ?? primaryQuoteParam ?? null;

const secondaryOrderbookForHeader = isComparison ? rightOrderbook : null;
const secondaryHeaderBase =
  secondaryOrderbookForHeader?.base ?? secondaryBaseParam ?? null;
const secondaryHeaderQuote =
  secondaryOrderbookForHeader?.quote ?? secondaryQuoteParam ?? null;

const arbitrageSummary = useMemo(() => {
  if (!isComparison || !leftOrderbook || !rightOrderbook) {
    return null;
  }

  const opportunity = pairStates.primary.opportunity;
  const effectiveConversion =
    pairStates.conversionRate ??
    opportunity?.quote_conversion_rate ??
    (leftOrderbook.quote === rightOrderbook.quote ? 1 : null);



  const formatPercent = (value: number) => {
    const formatted = formatPercentage(Math.abs(value));
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };
  const percentColor = (value: number) =>
    value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.secondary';

  if (opportunity) {
    const totalSellValue = opportunity.sell_levels.reduce(
      (sum, level) => sum + level.price * level.volume,
      0,
    );
    const totalBuyValue = opportunity.buy_levels.reduce(
      (sum, level) => sum + level.price * level.volume,
      0,
    );

    const conversionForBuyValue =
      typeof opportunity.quote_conversion_rate === 'number' &&
      Number.isFinite(opportunity.quote_conversion_rate) &&
      opportunity.quote_conversion_rate > 0
        ? opportunity.quote_conversion_rate
        : typeof effectiveConversion === 'number' && Number.isFinite(effectiveConversion) && effectiveConversion > 0
          ? effectiveConversion
          : 1;
    const normalizedBuyValue = totalBuyValue * conversionForBuyValue;
    const totalBaseVolume = opportunity.buy_levels.reduce(
      (sum, level) => sum + level.volume,
      0,
    );
    const normalizedQuoteChar =
      rightOrderbook.quote.startsWith('KRW')
        ? 'K'
        : rightOrderbook.quote.startsWith('BTC')
          ? 'B'
          : 'U';
    const profitRatioFromLevels =
      normalizedBuyValue > 0 ? totalSellValue / normalizedBuyValue : 1;
    const profitPercent = (profitRatioFromLevels - 1) * 100;
    // const volume = enriched.max_volume ?? opportunity.max_volume ?? 0;

    return (
      <Stack spacing={1.5} alignItems="center">
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h6"
            color={percentColor(profitPercent)}
            sx={{
              lineHeight: 1.1,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
            }}
          >
            {formatPercent(profitPercent)}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'center', md: 'center' }}
          justifyContent="center"
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: { xs: 'center', md: 'center' } }}
          >
            {t('orderbookPage.summary.volume', {
              volume: formatVolume(totalBaseVolume, 6),
              base: leftOrderbook.base,
            })}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: { xs: 'center', md: 'center' } }}
          >
            {t('orderbookPage.summary.notional', {
              value: formatTotalValue(normalizedBuyValue, true, normalizedQuoteChar),
              quote: rightOrderbook.quote,
            })}
          </Typography>
          {effectiveConversion && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: { xs: 'center', md: 'center' } }}
            >
              {t('orderbookPage.summary.conversion', {
                fromQuote: leftOrderbook.quote,
                toQuote: rightOrderbook.quote,
                rate: effectiveConversion.toFixed(8),
              })}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  }

  const bestAsk = leftOrderbook.asks[0]?.price;
  const bestBid = rightOrderbook.bids[0]?.price;

  if (!effectiveConversion || !bestAsk || !bestBid) {
    return null;
  }

  const spreadRatio = bestBid / (bestAsk * effectiveConversion);
  const spreadPercent = (spreadRatio - 1) * 100;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', md: 'center' }}
    >
      <Typography variant="h6" color={percentColor(spreadPercent)}>
        {formatPercent(spreadPercent)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('orderbookPage.summary.conversion', {
          fromQuote: leftOrderbook.quote,
          toQuote: rightOrderbook.quote,
          rate: effectiveConversion.toFixed(8),
        })}
      </Typography>
    </Stack>
  );
}, [
  isComparison,
  leftOrderbook,
  rightOrderbook,
  pairStates.primary.opportunity,
  pairStates.conversionRate,
  t,
]);
  const lastUpdatedTimestamp = cards
    .map((card) => card.state.orderbook?.timestamp ?? 0)
    .filter((timestamp) => timestamp > 0)
    .reduce((max, ts) => Math.max(max, ts), 0);

  const lastUpdatedLabel = lastUpdatedTimestamp
    ? formatTimestamp(lastUpdatedTimestamp, i18n.language)
    : t('orderbookPage.notAvailable');

  const pageTitle = isComparison
    ? null
    : t('orderbookPage.title', {
        exchange: primaryMeta.exchange ?? '',
        pair: primaryMeta.pair ?? '',
      });

  const handleToggleAutoRefresh = () => {
    setAutoRefreshEnabled((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY_AUTO_REFRESH, String(next));
      }
      return next;
    });
  };

  const handleSwapOrderbooks = useCallback(() => {
    if (!isComparison) {
      return;
    }

    if (isSymbolComparisonRoute) {
      if (
        !secondaryExchangeParam ||
        !secondarySymbolParam ||
        !primaryExchangeParam ||
        !primarySymbolParam
      ) {
        return;
      }

      const targetExchange = secondaryExchangeParam;
      const targetSymbol = params.symbol2 ?? secondarySymbolParam;
      const sourceExchange = primaryExchangeParam;
      const sourceSymbol = params.symbol1 ?? primarySymbolParam;

      navigate(`/orderbooks/${targetExchange}/${targetSymbol}/${sourceExchange}/${sourceSymbol}`);
      return;
    }

    if (
      secondaryExchangeParam &&
      secondaryBaseParam &&
      secondaryQuoteParam &&
      primaryExchangeParam &&
      primaryBaseParam &&
      primaryQuoteParam
    ) {
      navigate(
        `/orderbooks/${secondaryExchangeParam}/${secondaryBaseParam}/${secondaryQuoteParam}/${primaryExchangeParam}/${primaryBaseParam}/${primaryQuoteParam}`,
      );
    }
  }, [
    isComparison,
    isSymbolComparisonRoute,
    navigate,
    params.symbol1,
    params.symbol2,
    primaryBaseParam,
    primaryExchangeParam,
    primaryQuoteParam,
    primarySymbolParam,
    secondaryBaseParam,
    secondaryExchangeParam,
    secondaryQuoteParam,
    secondarySymbolParam,
  ]);

  const handleDepthChange = (event: SelectChangeEvent<string>) => {
    const value = Number(event.target.value);
    if (!Number.isNaN(value)) {
      const clamped = Math.min(Math.max(value, 1), MAX_LEVELS_DISPLAYED);
      setDepthLimit(clamped);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY_DEPTH, String(clamped));
      }
    }
  };

  const renderExchangeHeader = (
    exchangeIndex: number,
    exchangeLabel?: string,
    pairLabel?: string | null,
    pairVariant: 'h3' | 'h4' = 'h3',
    base?: string | null,
    quote?: string | null,
    isDisplayFutures = false,
  ) => {
    if (!exchangeLabel && !pairLabel) {
      return null;
    }
    const logoSrc = exchangeIndex >= 0 ? getExchangeLogo(exchangeIndex) : undefined;
    const fallbackText = (exchangeLabel || pairLabel || '').slice(0, 2).toUpperCase();
    const upperBase = base ? base.toUpperCase() : undefined;
    const upperQuote = quote ? quote.toUpperCase() : undefined;
    const tradingUrl =
      upperBase && upperQuote && exchangeIndex >= 0
        ? ExchangeUrls.getSpotTradeUrl(exchangeIndex, upperBase, upperQuote)
        : null;

    const handleOpenTrading = () => {
      if (!tradingUrl) {
        return;
      }
      window.open(tradingUrl, '_blank', 'noopener,noreferrer');
    };

    const headerContent = (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1.5}
        sx={{ textAlign: 'center' }}
      >
        <Avatar
          src={logoSrc}
          alt={exchangeLabel ? `${exchangeLabel} logo` : undefined}
          sx={{
            width: pairVariant === 'h3' ? 56 : 48,
            height: pairVariant === 'h3' ? 56 : 48,
            border: '1px solid rgba(255,255,255,0.12)',
            backgroundColor: 'rgba(255,255,255,0.08)',
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'common.white',
            filter: isDisplayFutures ? 'grayscale(100%) brightness(1.8)' : 'none',
          }}
        >
          {fallbackText}
        </Avatar>
        <Stack spacing={0.25} alignItems="center">
          {pairLabel && (
            <Typography
              variant={pairVariant}
              fontWeight={600}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {pairLabel}
            </Typography>
          )}
          {exchangeLabel && (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: '0.24em' }}
            >
              {exchangeLabel}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
    return (
      <Box
        onClick={tradingUrl ? handleOpenTrading : undefined}
        sx={{
          cursor: tradingUrl ? 'pointer' : 'default',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          transition: 'background-color 0.2s ease, transform 0.2s ease',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': tradingUrl
            ? {
                backgroundColor: 'rgba(0, 255, 255, 0.08)',
                transform: 'translateY(-2px)',
              }
            : undefined,
        }}
      >
        {headerContent}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 72, sm: 84 },
          right: { xs: 16, sm: 24 },
          zIndex: 1090,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'stretch',
          width: { xs: 180, sm: 220 },
          backgroundColor: 'rgba(2, 10, 26, 0.75)',
          borderRadius: 2,
          border: '1px solid rgba(0, 255, 255, 0.2)',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
          backdropFilter: 'blur(12px)',
          padding: { xs: 1, sm: 1.25 },
        }}
      >
        <FormControl
          size="small"
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255,255,255,0.05)',
              color: 'common.white',
              '& fieldset': { borderColor: 'rgba(0,255,255,0.4)' },
              '&:hover fieldset': { borderColor: 'rgba(0,255,255,0.6)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(0,255,255,0.9)' },
            },
            '& .MuiSvgIcon-root': {
              color: 'primary.light',
            },
          }}
        >
          <InputLabel
            id="orderbook-depth-label"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              '&.Mui-focused': { color: 'rgba(0,255,255,0.9)' },
            }}
          >
            {t('orderbookPage.depthLabel')}
          </InputLabel>
          <Select
            labelId="orderbook-depth-label"
            value={String(depthLimit)}
            label={t('orderbookPage.depthLabel')}
            onChange={handleDepthChange}
            sx={{
              color: 'common.white',
              '& .MuiSelect-icon': {
                color: 'rgba(0,255,255,0.9)',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'rgba(10,14,28,0.95)',
                  color: 'common.white',
                },
              },
            }}
          >
            {Array.from({ length: MAX_LEVELS_DISPLAYED }, (_, index) => index + 1).map(
              (count) => (
                <MenuItem key={count} value={count}>
                  {t('orderbookPage.depthOption', { count })}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
        <Button
          onClick={handleToggleAutoRefresh}
          variant={autoRefreshEnabled ? 'contained' : 'outlined'}
          color={autoRefreshEnabled ? 'success' : 'primary'}
          startIcon={<AutorenewIcon />}
          sx={{
            width: '100%',
            whiteSpace: 'nowrap',
          }}
        >
          {autoRefreshEnabled
            ? t('orderbookPage.autoRefreshDisable')
            : t('orderbookPage.autoRefreshEnable')}
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Stack spacing={3}>
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          {isComparison && secondaryMeta ? (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1.5, md: 3 }}
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              {renderExchangeHeader(
                primaryExchangeIndex,
                primaryMeta.exchange,
                primaryMeta.pair,
                'h4',
                primaryHeaderBase,
                primaryHeaderQuote,
                isFutures,
              )}
              <Box
                component="button"
                type="button"
                onClick={handleSwapOrderbooks}
                sx={{
                  border: 'none',
                  background: 'rgba(255, 255, 255, 0.04)',
                  color: 'inherit',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3em',
                  padding: '0.5rem 1rem',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    background: 'rgba(0, 255, 255, 0.12)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 20px rgba(0, 255, 255, 0.15)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(0, 255, 255, 0.6)',
                    outlineOffset: 2,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  component="span"
                  fontWeight={500}
                  sx={{ letterSpacing: 'inherit', textTransform: 'inherit' }}
                >
                  {t('orderbookPage.comparisonVS')}
                </Typography>
              </Box>
              {renderExchangeHeader(
                secondaryExchangeIndex,
                secondaryMeta.exchange,
                secondaryMeta.pair,
                'h4',
                secondaryHeaderBase,
                secondaryHeaderQuote,
                isFutures,
              )}
            </Stack>
          ) : (
            renderExchangeHeader(
              primaryExchangeIndex,
              primaryMeta.exchange,
              primaryMeta.pair,
              'h3',
              primaryHeaderBase,
              primaryHeaderQuote,
              isFutures,
            )
          )}
          {!isComparison && pageTitle && (
            <Typography variant="body2" color="text.secondary">
              {pageTitle}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t('orderbookPage.lastUpdated', {
              timestamp: lastUpdatedLabel,
            })}
          </Typography>
          {isComparison && arbitrageSummary}
        </Stack>

        <Stack
          spacing={2}
          direction={{ xs: 'column', xl: isComparison ? 'row' : 'column' }}
          sx={{ width: '100%' }}
        >
          {cards.map((card) => {
            const orderbook = card.state.orderbook;
            let base: string | undefined | null = null;
            let quote: string | undefined | null = null;
            const orderbookExchange = orderbook?.exchange ?? card.meta.exchange ?? null;

            if (card.key === 'primary') {
              base = orderbook?.base ?? primaryBaseParam;
              quote = orderbook?.quote ?? primaryQuoteParam;
            } else { // secondary
              base = orderbook?.base ?? secondaryBaseParam;
              quote = orderbook?.quote ?? secondaryQuoteParam;
            }

            const normalizedExchange = orderbookExchange ? orderbookExchange.toLowerCase() : null;
            const normalizedBase = base ? base.toUpperCase() : null;
            const normalizedQuote = quote ? quote.toUpperCase() : null;

            return (
              <Stack key={card.key} spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                {normalizedExchange && normalizedBase && normalizedQuote && (
                  <TradingChart
                    exchange={normalizedExchange}
                    base={normalizedBase}
                    quote={normalizedQuote}
                    isFutures={isFutures}
                  />
                )}
                <OrderbookCard
                  meta={card.meta}
                  state={card.state}
                  headers={headers}
                  noDataTitle={t('orderbookPage.noDataTitle')}
                  noDataDescription={t('orderbookPage.noDataDescription')}
                  t={t}
                  isFutures={isFutures}
                />
              </Stack>
            );
          })}
        </Stack>
        </Stack>
      </Container>
    </>
  );
};

export default OrderbookPage;
